import PlacesAutocomplete, {
  Suggestion,
  geocodeByAddress,
} from "react-places-autocomplete";
import { useState, useEffect, useMemo, useRef } from "react";
import { Input, InputGroup } from "rsuite";

const padArray = (array: any[], size: number) => {
  while (array.length < size) {
    array.push("");
  }
  return array;
};

const getGeoCode = async (placeId: string) => {
  if (placeId) {
    const response = await geocodeByAddress(placeId);
    // return response[0];
    const { long_name: postalCode = "" } =
      response[0].address_components.find((c) =>
        c.types.includes("postal_code")
      ) || {};
    return postalCode;
  } else return null;
};

const SuggestionBox = ({
  suggestions,
  getSuggestionItemProps,
  loading,
}: {
  suggestions: readonly Suggestion[];
  getSuggestionItemProps: any;
  loading: boolean;
}) => {
  const [geoCodes, setGeoCodes] = useState<
    (google.maps.GeocoderResult | null | string)[]
  >([]);

  const placeIds = useMemo(
    () =>
      padArray(
        suggestions.map((s) => s.description),
        5
      ),
    [suggestions]
  );

  useEffect(() => {
    Promise.all(placeIds.map(getGeoCode)).then((results) => {
      setGeoCodes(results);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...placeIds]);

  return (
    <div
      className={`position-absolute ${
        suggestions.length || loading
          ? "border border-1 location-search-suggestions"
          : ""
      }`}
    >
      {loading && <div className="p-2">Loading...</div>}
      {suggestions.map((suggestion, idx) => {
        const style = {
          backgroundColor: suggestion.active ? "#00bdb4" : "#fff",
          color: suggestion.active ? "#fff" : "#000",
        };
        return (
          <div
            {...getSuggestionItemProps(suggestion, {
              style,
              className: "p-2 click",
            })}
            key={suggestion.id}
          >
            {suggestion.description}{" "}
            {geoCodes[idx] !== "" && <b> | {geoCodes[idx]}</b>}
          </div>
        );
      })}
    </div>
  );
};

export const LocationSearchInput = ({
  onSelect,
  value,
  onBlur,
}: {
  onSelect: (address: string) => void;
  value?: string;
  onBlur?: () => void;
}) => {
  const [address, setAddress] = useState(value ?? "");
  const [postalCodeUI, setPostalCodeUI] = useState<string | null>();
  const timeoutId = useRef<number>();

  useEffect(() => {
    setAddress(value ?? "");
    value && getGeoCode(value!).then((res) => setPostalCodeUI(res));
  }, [value]);

  const getPostalCode = (address: string) => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(
      () => getGeoCode(address).then((res) => setPostalCodeUI(res)),
      500
    ) as unknown as number;
  };

  const handleChange = (address: string) => {
    setAddress(address);
    getPostalCode(address);
    onSelect("");
  };

  const handleSelect = (address: string) => {
    setAddress(address);
    getPostalCode(address);
    onSelect(address);
  };

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
      debounce={500}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className="position-relative">
          <InputGroup>
            <Input
              autoComplete="off"
              onChange={(e) =>
                getInputProps().onChange({ target: { value: String(e) } })
              }
              onKeyDown={getInputProps().onKeyDown}
              onBlur={(e) => {
                if (suggestions.length) {
                  handleSelect(suggestions[0].description);
                }
                getInputProps().onBlur(e);
                onBlur?.();
              }}
              value={address}
              // className="position-absolute"
            />
            <InputGroup.Addon>{postalCodeUI}</InputGroup.Addon>
          </InputGroup>

          <SuggestionBox
            suggestions={suggestions}
            getSuggestionItemProps={getSuggestionItemProps}
            loading={loading}
          />
        </div>
      )}
    </PlacesAutocomplete>
  );
};
